import React, { useState } from 'react';
import { ContainerHeader } from './ContainerHeader';
import { NestedContainer } from '../NestedContainer/NestedContainer';
import { AddNestedHorizontal } from '../NestedContainer/AddNestedHorizontal';
import { Container as ContainerType } from '../../../types/canvas.types';
import ContainerContent from './ContainerContent';
import { useEditor } from '../../../contexts/EditorContext';

interface ContainerProps {
  container: ContainerType;
  isSelected: boolean;
  onSelect: (containerId: string) => void;
  onAddNestedHorizontal: (parentId: string) => void;
  onAddNestedVertical: (parentId: string, rowIndex: number) => void;
  onAddNestedBetween?: (parentId: string, beforeIndex?: number) => void;
  onRemoveComponent?: (componentId: string, containerId: string) => void;
  onFieldSelect?: (fieldId: string, fieldType: string, containerId: string, parentId?: string) => void;
  selectedFieldId?: string | null;
  onSubmodelSelect?: (submodelId: string, containerId?: string) => void;
  onWidgetSelect?: (widgetId: string, widgetType: string, configuration?: any, containerId?: string) => void;
  onAddContainer?: (afterContainerId: string) => void;
  onDuplicateContainer?: (containerId: string) => void;
  onDeleteContainer?: (containerId: string) => void;
  onContentUpdate?: (widgetId: string, content: string) => void;
}

export const Container = ({ container, isSelected, onSelect, onAddNestedHorizontal, onAddNestedVertical, onAddNestedBetween,onRemoveComponent,onFieldSelect,selectedFieldId,onSubmodelSelect,onWidgetSelect, onAddContainer, onDuplicateContainer, onContentUpdate, onDeleteContainer }: ContainerProps) => {
  const { editorState, removeComponentFromContainer, saveWidgetConfig } = useEditor();
  // Initialize collapsed state based on container configuration
  const [isCollapsed, setIsCollapsed] = useState(container.configuration.collapsible ? false : false);
  
  const handleContainerClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    onSelect(container.id);
  };

  const handleRemoveComponent = (componentId: string) => {
    if (onRemoveComponent && componentId !== 'empty') {
      onRemoveComponent(componentId, container.id);
    }
  };

  const handleRemoveYourBuildWidget = () => {
    const yourBuildWidget = container.content.items?.find(
      item => item.type === 'widget' && item.widgetType === 'yourBuild'
    );
    
    if (yourBuildWidget) {
      // Save configuration before removing
      if (yourBuildWidget.configuration) {
        saveWidgetConfig('your-build-widget', yourBuildWidget.configuration);
      }
      
      // Remove from container
      removeComponentFromContainer(yourBuildWidget.id, container.id);
    }
  };

  const handleFieldSelect = (fieldId: string, fieldType: string, parentId?: string) => {
    if (onFieldSelect) {
      onFieldSelect(fieldId, fieldType, container.id, parentId);
    }
  };

  // Check if any nested container is selected (for parent highlighting)
  const hasSelectedNestedContainer = () => {
    if (!container.content.nestedContainers || container.content.nestedContainers.length === 0) {
      return false;
    }
    
    // check if any nested container is selected(Recursively)
    const checkNestedContainers = (containers: ContainerType[]): boolean => {
      for (const nestedContainer of containers) {
        if (nestedContainer.id === editorState.selectedContainerId) {
          return true;
        }
        
        if (nestedContainer.content.nestedContainers && nestedContainer.content.nestedContainers.length > 0) {
          if (checkNestedContainers(nestedContainer.content.nestedContainers)) {
            return true;
          }
        }
      }
      return false;
    };
    
    const result = checkNestedContainers(container.content.nestedContainers);
    if (result) {
      console.log("[DEBUG] Container", container.id, "contains the selected nested container");
    }
    return result;
  };

  const getSelectionLevel = (containerId: string, editorState: any): 'primary' | 'secondary' | 'tertiary' | null => {
    // Direct selection
    if (editorState.selectedContainerId === containerId) {
      return 'primary';
    }
    
    // Check if in selection path
    const pathIndex = editorState.selectionPath.indexOf(containerId);
    if (pathIndex === -1) return null;
    
    // Determine level based on position in path
    const pathLength = editorState.selectionPath.length;
    const distanceFromEnd = pathLength - pathIndex - 1;
    
    if (distanceFromEnd === 1) return 'secondary';
    if (distanceFromEnd === 2) return 'tertiary';
    
    return 'tertiary';
  };
  
  
  const getContainerHighlightClass = () => {
  const selectionLevel = getSelectionLevel(container.id, editorState);
  
  if (selectionLevel === 'primary') {
    return 'tw-border-[#1F7AE4] tw-shadow-sm tw-bg-[#F4F5F7]';
  } else if (selectionLevel === 'secondary') {
    return 'tw-border-[#90CAF9] tw-bg-[#F8FBFF]';
  } else if (selectionLevel === 'tertiary') {
    return 'tw-border-[#E3F2FD] tw-bg-[#FAFBFF]';
  } else if (hasSelectedNestedContainer()) {
    return 'tw-border-[#90CAF9] tw-bg-[#F8FBFF]';
  }
  
  return 'tw-border-[#DADBDD] tw-bg-white';
};

  const getAlignmentClass = () => {
    switch (container.configuration.alignment) {
      case 'center': return 'tw-mx-auto';
      case 'right': return 'tw-ml-auto';
      case 'left':
      default: return '';
    }
  };

  const getWidthStyle = () => {
    if (container.configuration.width && container.configuration.width.toString() !== '100%') {
      if (container.configuration.width.toString().endsWith('%')) {
        const widthValue = parseInt(container.configuration.width.toString());
        const safeWidth = Math.max(widthValue, 25);
        return { width: `${safeWidth}%` };
      }
      
      if (container.configuration.width.toString().endsWith('px')) {
        const widthValue = parseInt(container.configuration.width.toString());
        const safeWidth = Math.max(widthValue, 100);
        return { width: `${safeWidth}px` };
      }
      
      if (!isNaN(Number(container.configuration.width))) {
        const widthValue = parseInt(container.configuration.width.toString());
        const safeWidth = Math.max(widthValue, 25);
        return { width: `${safeWidth}%` };
      }
    }
    
    return { width: '100%' };
  };

  const getHeightStyle = () => {
    if (container.configuration.height && container.configuration.height !== 'auto' && container.configuration.height !== 'Dynamic') {
      if (container.configuration.height.endsWith('px')) {
        return { height: container.configuration.height };
      }
      
      if (!isNaN(Number(container.configuration.height))) {
        return { height: `${container.configuration.height}px` };
      }
    }
    return {};
  };

  const handleNestedContainerSelect = (nestedId: string) => {
    onSelect(nestedId);
  };

  const handleSubmodelSelect = (submodelId: string) => {
    if (onSubmodelSelect) {
      onSubmodelSelect(submodelId, container.id);
    }
  };

  const handleWidgetSelect = (widgetId: string, widgetType: string, configuration?: any) => {
    if (onWidgetSelect) {
      onWidgetSelect(widgetId, widgetType, configuration, container.id);
    }
  };

  const baseId = `container-${container.id}`;

  return (
    <div
      id={baseId}
      data-testid={baseId}
      className={`tw-border ${getContainerHighlightClass()} tw-mb-1 ${
        container.configuration.width && container.configuration.width !== '100%' ? '' : 'tw-flex-1'
      }`}
      onClick={(e) => {
        handleContainerClick(e);
      }}
      style={getWidthStyle()}
      data-container-id={container.id}
    >
      {container.configuration.showTitle && (
        <ContainerHeader
          id={`${baseId}-header`}
          name={container.name}
          containerId={container.id}
          componentId={container.id}
          containerType={container.content.type}
          widgetType={container.content['widget-type']}
          hasNestedContainers={container.content.nestedContainers && container.content?.nestedContainers.length > 0}
          containerItems={container.content.items}
          nestingLevel={0}  // Top-level containers have nestingLevel 0
          onRemoveComponent={handleRemoveComponent}
          onAddContainer={() => onAddContainer?.(container.id)}
          onDuplicateContainer={() => onDuplicateContainer?.(container.id)}
          onDeleteContainer={() => onDeleteContainer?.(container.id)}
          onRemoveYourBuildWidget={handleRemoveYourBuildWidget}
          collapsible={container.configuration.collapsible || false}
          isCollapsed={isCollapsed}
          onToggleCollapse={() => setIsCollapsed(!isCollapsed)}
        />
      )}

      {!isCollapsed && (
        <div
          id={`${baseId}-content-area`}
          className={`tw-overflow-auto`}
          style={{
            ...getHeightStyle(),
            width: '100%',
            display: 'flex',
            flexDirection: 'column',
            minWidth: '100%'
          }}
          onClick={(e) => {
           // console.log("[DEBUG] Container content area clicked, container ID:", container.id);
          }}
        >
        {/* Content wrapper with alignment and padding */}
        <div id={`${baseId}-content-wrapper`} className={`tw-px-4 ${getAlignmentClass()}`}>
          {/* Content container with layout classes applied */}
          <div id={`${baseId}-content-container`} className={`${container.configuration.layout === 'horizontal' ? 'tw-flex tw-flex-row tw-flex-wrap tw-gap-4' : ''} tw-w-full`}>
            <ContainerContent
              id={`${baseId}-content`}
              items={container.content.items}
              componentId={container.content.componentId}
              usedComponents={container.usageInfo.usedComponents}
              containerConfig={container.configuration}
              onFieldSelect={handleFieldSelect}
              selectedFieldId={editorState.selectedFieldId}
              submodel={container?.content?.submodel}
              onSubmodelSelect={handleSubmodelSelect}
              selectedSubmodelId={editorState.selectedSubmodelId}
              selectedWidgetId={editorState.selectedWidgetId}
              onWidgetSelect={handleWidgetSelect}
              onContentUpdate={onContentUpdate}
            />
          </div>
        </div>

        {/* Horizontal line AFTER content - now outside the padded area */}
        {(!container.content.nestedContainers || container.content.nestedContainers.length === 0) && (
          <div id={`${baseId}-add-nested-wrapper`} className="tw-px-4">
            <AddNestedHorizontal
              id={`${baseId}-add-nested-horizontal`}
              parentId={container.id}
              onAdd={(parentId) => {
                onAddNestedHorizontal(parentId);
              }}
            />
          </div>
        )}

        {/* Nested containers - also with padding wrapper */}
        {container.content.nestedContainers && container.content.nestedContainers.length > 0 && (
          <div id={`${baseId}-nested-wrapper`} className="tw-px-4">
            <NestedContainer
            id={`${baseId}-nested-container`}
            parentId={container.id}
            nestedContainers={container.content.nestedContainers}
            selectedContainerId={editorState.selectedContainerId}
            onSelect={(nestedId) => {

                handleNestedContainerSelect(nestedId);
            }}
            onAddNestedHorizontal={onAddNestedHorizontal}
            onAddNestedVertical={(parentId, rowIndex) => {
                // This handler now receives rowIndex
                if (onAddNestedVertical) {
                    onAddNestedVertical(parentId, rowIndex ?? -1);
                }
            }}
            onAddNestedBetween={onAddNestedBetween}
            onRemoveComponent={(componentId, containerId) => {
                if (onRemoveComponent) {
                    onRemoveComponent(componentId, containerId);
                }
            }}
            nestingLevel={1}
            containerConfig={container.configuration}
            onFieldSelect={handleFieldSelect}
            selectedFieldId={selectedFieldId}
            onSubmodelSelect={handleSubmodelSelect}
            selectedSubmodelId={editorState.selectedSubmodelId}
            onWidgetSelect={handleWidgetSelect}
            onAddContainer={onAddContainer}
            onDuplicateContainer={onDuplicateContainer}
            onDeleteContainer={onDeleteContainer}
            onContentUpdate={onContentUpdate}
            />
          </div>
        )}
        </div>
      )}
    </div>
  );
};